import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Loader2, Search, Mail, Users, Crown, Flame, TrendingUp, Shield, BookOpen, GraduationCap, Send, RotateCcw, CalendarClock, Mic, Gem, Trash2, Settings } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [resetLoading, setResetLoading] = useState(null);
  const [resetPracticesLoading, setResetPracticesLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sendingReports, setSendingReports] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [features, setFeatures] = useState([]);
  const [featureLoading, setFeatureLoading] = useState({});

  const handleSendWeeklyReports = async () => {
    if (!window.confirm('Send the weekly progress report to every parent whose child practised in the last 7 days?')) return;
    setSendingReports(true);
    setReportResult(null);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/reports/weekly/send-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setReportResult(data);
      toast.success(`Sent ${data.sent} weekly reports · ${data.failed} failed`);
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setSendingReports(false);
    }
  };

  const handleSendSelfReport = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/reports/weekly/me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Sample report sent to ${data.email}`);
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchFeatureFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await api.adminListUsers();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureFlags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setFeatures(data.features || []);
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const toggleFeature = async (featureName, currentState) => {
    setFeatureLoading(prev => ({ ...prev, [featureName]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/features/${featureName}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ enabled: !currentState })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchFeatureFlags(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle feature');
      }
    } catch (error) {
      console.error('Error toggling feature:', error);
      toast.error('Failed to toggle feature');
    } finally {
      setFeatureLoading(prev => ({ ...prev, [featureName]: false }));
    }
  };

  const handleTriggerReset = async (userId, email) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    setResetLoading(userId);
    try {
      const result = await api.adminResetUserPassword(userId);
      if (result.email_sent) {
        toast.success(`Reset email sent to ${email}`);
      } else {
        toast.warning(result.message || 'Reset link generated but email delivery failed');
      }
    } catch (error) {
      console.error('Error triggering reset:', error);
      toast.error('Failed to trigger password reset');
    } finally {
      setResetLoading(null);
    }
  };

  const handleResetPractices = async (userId, email) => {
    if (!window.confirm(
      `Reset free-practice quota for ${email}?\n\n` +
      `This clears every "50 free questions per topic" counter back to 0. ` +
      `Progress, streaks, subscriptions and history are NOT affected.`
    )) return;
    setResetPracticesLoading(userId);
    try {
      const result = await api.adminResetUserPractices(userId);
      toast.success(
        `Free practices reset for ${email} (${result.topics_reset} topic${result.topics_reset === 1 ? '' : 's'} cleared)`
      );
    } catch (error) {
      console.error('Error resetting practices:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset free practices');
    } finally {
      setResetPracticesLoading(null);
    }
  };

  const handleToggleRole = async (userId, currentRole, email) => {
    const newRole = currentRole === 'teacher' ? 'student' : 'teacher';
    const action = newRole === 'teacher' ? 'grant teacher role to' : 'revoke teacher role from';
    if (!window.confirm(`${action} ${email}?`)) return;
    try {
      await api.adminUpdateRole(userId, newRole);
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      console.error('Role update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(
      `⚠️ PERMANENTLY DELETE ${email}?\n\n` +
      `This will PERMANENTLY remove:\n` +
      `• User account\n` +
      `• All progress and statistics\n` +
      `• Subscriptions and purchases\n` +
      `• Tutor worksheets (if tutor)\n` +
      `• All related data\n\n` +
      `THIS CANNOT BE UNDONE. Are you absolutely sure?`
    )) return;
    
    // Second confirmation
    if (!window.confirm(`Type YES to confirm deletion of ${email}`)) {
      toast.info('Deletion cancelled');
      return;
    }
    
    setDeleteLoading(userId);
    try {
      const result = await api.adminDeleteUser(userId);
      toast.success(`User ${email} permanently deleted`);
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteLoading(null);
    }
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch = !search
      || u.email.toLowerCase().includes(search.toLowerCase())
      || u.name.toLowerCase().includes(search.toLowerCase());
    const matchesLevel = levelFilter === 'all' || u.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  const stats = {
    total,
    subscribed: users.filter(u => u.active_subscriptions.length > 0).length,
    active: users.filter(u => u.streak.current_streak > 0 && u.streak.last_practice_date).length,
    p4: users.filter(u => u.level === 'P4').length,
    p5: users.filter(u => u.level === 'P5').length,
    p6: users.filter(u => u.level === 'P6').length
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F8FAFC]">
        <Card className="p-8 rounded-3xl max-w-md text-center border-2 border-red-200" data-testid="admin-access-denied">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-red-700 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-6">You don&apos;t have permission to view this page.</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-8">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="rounded-full" data-testid="admin-back-button">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="admin-title">Admin Dashboard</h1>
                  <div className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                    <Shield className="w-3 h-3" strokeWidth={2.5} />
                    Admin
                  </div>
                </div>
                <p className="text-sm text-slate-500">Signed in as {user?.email}</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Quick Actions */}
        <div className="mb-6 flex flex-wrap gap-3">
          <Button
            onClick={() => navigate('/admin/questions')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-6 py-5 font-bold tactile-button border-emerald-700"
            data-testid="admin-manage-questions-button"
          >
            <BookOpen className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Manage Custom Questions
          </Button>
          <Button
            onClick={() => window.open('/lesson-planner/', '_blank', 'noopener,noreferrer')}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-5 font-bold tactile-button border-teal-800"
            data-testid="admin-lesson-planner-button"
          >
            <CalendarClock className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Tutor Lesson Planner
          </Button>
          <Button
            onClick={() => navigate('/admin/oral-passages')}
            className="bg-rose-600 hover:bg-rose-700 text-white rounded-full px-6 py-5 font-bold tactile-button border-rose-800"
            data-testid="admin-oral-passages-button"
          >
            <Mic className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Oral Reading Passages
          </Button>
          <Button
            onClick={() => navigate('/admin/marketplace')}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-6 py-5 font-bold tactile-button border-amber-800"
            data-testid="admin-marketplace-button"
          >
            <Gem className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Marketplace Vetting
          </Button>
          <Button
            onClick={() => navigate('/admin/qa-bot')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-5 font-bold tactile-button border-indigo-800"
            data-testid="admin-qa-bot-button"
          >
            <Shield className="w-4 h-4 mr-2" strokeWidth={2.5} />
            QA Bot
          </Button>
        </div>

        {/* Feature Flags Section */}
        <Card className="mb-6 p-6 rounded-3xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-slate-600" strokeWidth={2.5} />
            <h2 className="text-lg font-heading font-bold text-slate-800">Feature Flags</h2>
          </div>
          <div className="space-y-3">
            {features.map(feature => (
              <div 
                key={feature.feature} 
                className="flex items-center justify-between p-4 bg-white rounded-2xl border-2 border-slate-100"
                data-testid={`feature-flag-${feature.feature}`}
              >
                <div>
                  <p className="font-semibold text-slate-800">{feature.name || feature.feature}</p>
                  <p className="text-sm text-slate-500">
                    {feature.enabled ? '✓ Enabled' : '✗ Disabled'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {featureLoading[feature.feature] && (
                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                  )}
                  <Switch
                    checked={feature.enabled}
                    onCheckedChange={() => toggleFeature(feature.feature, feature.enabled)}
                    disabled={featureLoading[feature.feature]}
                    data-testid={`feature-toggle-${feature.feature}`}
                  />
                </div>
              </div>
            ))}
            {features.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No feature flags configured</p>
            )}
          </div>
        </Card>

        {loading ? (
          <div className="text-center py-16" data-testid="admin-loading">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600">Loading users...</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-emerald-500" strokeWidth={2.5} />
                  <span className="text-xs font-bold text-slate-600">Total</span>
                </div>
                <p className="text-2xl font-heading font-bold text-slate-800" data-testid="admin-stat-total">{stats.total}</p>
              </Card>
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4 text-amber-500" strokeWidth={2.5} />
                  <span className="text-xs font-bold text-slate-600">Premium</span>
                </div>
                <p className="text-2xl font-heading font-bold text-amber-600" data-testid="admin-stat-premium">{stats.subscribed}</p>
              </Card>
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-4 h-4 text-orange-500" strokeWidth={2.5} />
                  <span className="text-xs font-bold text-slate-600">Active</span>
                </div>
                <p className="text-2xl font-heading font-bold text-orange-600" data-testid="admin-stat-active">{stats.active}</p>
              </Card>
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-emerald-50">
                <span className="text-xs font-bold text-slate-600">P4</span>
                <p className="text-2xl font-heading font-bold text-emerald-700" data-testid="admin-stat-p4">{stats.p4}</p>
              </Card>
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-amber-50">
                <span className="text-xs font-bold text-slate-600">P5</span>
                <p className="text-2xl font-heading font-bold text-amber-700" data-testid="admin-stat-p5">{stats.p5}</p>
              </Card>
              <Card className="p-4 rounded-2xl border-2 border-slate-200 bg-blue-50">
                <span className="text-xs font-bold text-slate-600">P6</span>
                <p className="text-2xl font-heading font-bold text-blue-700" data-testid="admin-stat-p6">{stats.p6}</p>
              </Card>
            </div>

            {/* Weekly parent reports */}
            <Card className="p-4 md:p-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50 mb-6" data-testid="admin-weekly-reports">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-emerald-700" />
                    <h3 className="font-heading font-bold text-emerald-800">Weekly parent progress reports</h3>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Emailed to the registered account email every Saturday 9am Singapore time. Each report covers the week just completed (Sat 00:00 → Fri 23:59 SGT). You can also trigger a batch or a sample right now.
                  </p>
                  {reportResult && (
                    <div className="mt-2 text-xs bg-white rounded-lg px-3 py-2 border border-emerald-200" data-testid="admin-reports-result">
                      Last batch: <strong>{reportResult.sent}</strong> sent · <strong>{reportResult.failed}</strong> failed · {reportResult.eligible} eligible
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    onClick={handleSendSelfReport}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-emerald-700 border-emerald-300"
                    data-testid="admin-send-self-report"
                  >
                    <Mail className="w-3.5 h-3.5 mr-1.5" /> Preview to me
                  </Button>
                  <Button
                    onClick={handleSendWeeklyReports}
                    disabled={sendingReports}
                    size="sm"
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="admin-send-weekly-reports"
                  >
                    {sendingReports ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    {sendingReports ? 'Sending...' : 'Send now to all'}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Search & Filters */}
            <Card className="p-4 rounded-2xl border-2 border-slate-200 mb-6">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by email or name..."
                    className="w-full pl-10 pr-4 py-2.5 rounded-full border-2 border-slate-200 focus:border-emerald-500 focus:outline-none text-sm"
                    data-testid="admin-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  {['all', 'P4', 'P5', 'P6'].map(l => (
                    <button
                      key={l}
                      onClick={() => setLevelFilter(l)}
                      className={`px-4 py-2 rounded-full text-xs font-bold transition ${
                        levelFilter === l
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`admin-filter-${l}`}
                    >
                      {l === 'all' ? 'All' : l}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Users Table */}
            <Card className="rounded-2xl border-2 border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="admin-users-table">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Student</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Level</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Subscription</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Progress</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Streak</th>
                      <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase">Joined</th>
                      <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-slate-500" data-testid="admin-no-users">
                          No users match your filters
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u, idx) => {
                        const acc = u.progress.total_attempted > 0
                          ? Math.round((u.progress.total_correct / u.progress.total_attempted) * 100)
                          : 0;
                        return (
                          <tr
                            key={u.id}
                            className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                            data-testid={`admin-user-row-${idx}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                                  {u.name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800 text-sm flex items-center gap-1">
                                    {u.name}
                                    {u.is_admin && (
                                      <Shield className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />
                                    )}
                                    {u.role === 'teacher' && !u.is_admin && (
                                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
                                        <GraduationCap className="w-2.5 h-2.5" strokeWidth={2.5} />
                                        Teacher
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-slate-500" data-testid={`admin-user-email-${idx}`}>{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-block bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs font-bold">
                                {u.level}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {u.active_subscriptions.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {u.active_subscriptions.map((s, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">
                                      <Crown className="w-3 h-3" strokeWidth={2.5} />
                                      {s.level}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">Free</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs">
                                <p className="font-semibold text-slate-800">{u.progress.total_correct}/{u.progress.total_attempted}</p>
                                <p className="text-slate-500">{acc}% accuracy</p>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Flame className={`w-3.5 h-3.5 ${u.streak.current_streak > 0 ? 'text-orange-500 fill-orange-500' : 'text-slate-300'}`} strokeWidth={2.5} />
                                <span className="text-sm font-semibold text-slate-700">{u.streak.current_streak}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-600">
                              {formatDate(u.created_at)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex gap-1 justify-end">
                                {!u.is_admin && (
                                  <Button
                                    onClick={() => handleToggleRole(u.id, u.role, u.email)}
                                    variant="outline"
                                    size="sm"
                                    className={`rounded-full text-xs ${
                                      u.role === 'teacher'
                                        ? 'border-blue-300 text-blue-700 hover:bg-blue-50'
                                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                    }`}
                                    data-testid={`admin-role-toggle-${idx}`}
                                    title={u.role === 'teacher' ? 'Revoke teacher role' : 'Grant teacher role'}
                                  >
                                    <GraduationCap className="w-3 h-3 mr-1" strokeWidth={2.5} />
                                    {u.role === 'teacher' ? 'Revoke' : 'Make Teacher'}
                                  </Button>
                                )}
                                <Button
                                  onClick={() => handleResetPractices(u.id, u.email)}
                                  variant="outline"
                                  size="sm"
                                  disabled={resetPracticesLoading === u.id}
                                  className="rounded-full text-xs text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                                  title="Reset the 50-per-topic free practice quota for this student"
                                  data-testid={`admin-reset-practices-button-${idx}`}
                                >
                                  {resetPracticesLoading === u.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <RotateCcw className="w-3 h-3 mr-1" />
                                      Reset practices
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handleTriggerReset(u.id, u.email)}
                                  variant="outline"
                                  size="sm"
                                  disabled={resetLoading === u.id}
                                  className="rounded-full text-xs"
                                  data-testid={`admin-reset-button-${idx}`}
                                >
                                  {resetLoading === u.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Mail className="w-3 h-3 mr-1" />
                                      Reset
                                    </>
                                  )}
                                </Button>
                                {!u.is_admin && (
                                  <Button
                                    onClick={() => handleDeleteUser(u.id, u.email)}
                                    variant="outline"
                                    size="sm"
                                    disabled={deleteLoading === u.id}
                                    className="rounded-full text-xs text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800"
                                    title="Permanently delete this user and all their data"
                                    data-testid={`admin-delete-button-${idx}`}
                                  >
                                    {deleteLoading === u.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <>
                                        <Trash2 className="w-3 h-3 mr-1" />
                                        Delete
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            <p className="text-xs text-slate-500 text-center mt-4">
              Showing {filteredUsers.length} of {total} users. Passwords are securely hashed with bcrypt and cannot be viewed — only reset.
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
